import { call, CallEffect, put, PutEffect, select, SelectEffect, debounce, takeLatest } from 'redux-saga/effects';

import { AUTH_SUCCESS } from 'js/actionCreators/authActions';
import { showError } from 'js/actionCreators/uiActions';
import { APP_DATA_KEYS } from 'js/config/consts';
import {
  setLastSynced,
  setSettingAppliedTo,
  setAnimationType,
  setAnimationConfiguration,
  setAnimationDuration,
  AnimationStageSetting,
  loadSettingsSuccess
} from 'js/reducers/userAnimationSettings';
import { appServices } from 'js/shared/helpers/app-services/AppServices';
import { HTTPError, RootState } from 'js/types';

export const getUserAnimationsSettings = (state: RootState) => state.userAnimationSettings.stageSettings;

export function* syncSettings(): Generator<SelectEffect | CallEffect | PutEffect, void, AnimationStageSetting> {
  try {
    const settings = yield select(getUserAnimationsSettings);
    yield call({ fn: appServices.setAppData, context: appServices }, APP_DATA_KEYS.ANIMATION_SETTINGS, settings);
    yield put(setLastSynced(Date.now()));
  } catch (error) {
    yield put(
      showError({
        message: 'Error syncing user animation settings. Please try again later.',
        description: error instanceof Error ? error.message : undefined
      })
    );
    console.error('Error syncing user animation settings:', error);
    return;
  }
}

export function* loadSettings(): Generator<CallEffect | PutEffect, void, AnimationStageSetting> {
  try {
    const settings = yield call({ fn: appServices.getAppData, context: appServices }, APP_DATA_KEYS.ANIMATION_SETTINGS);
    yield put(loadSettingsSuccess(settings));
  } catch (error) {
    if ((error as HTTPError).httpStatusCode === 404) {
      // User hasn't stored any animation settings yet so we can skip the error
      return;
    }

    console.error('Error loading user animation settings:', error);
    yield put(
      showError({
        message: 'Error loading user animation settings. Please try again later.',
        description: error instanceof Error ? error.message : undefined
      })
    );
  }
}

export const SETTING_SYNC_DELAY = 2000;
export default function* userAnimationSettingsSaga() {
  yield debounce(
    SETTING_SYNC_DELAY,
    [setSettingAppliedTo.type, setAnimationType.type, setAnimationConfiguration.type, setAnimationDuration.type],
    syncSettings
  );
  yield takeLatest(AUTH_SUCCESS, loadSettings);
}
